/**
 * SourceBody — one streamed document, rendered as whatever it actually is.
 *
 * The shared multi-format renderer, extracted rather than adopting Matters'
 * `ReaderComponent` wholesale. This is the BODY only: no chrome, no
 * download/delete toolbar, no matter-scoped props — so each surface keeps its
 * own frame around it. (`DocumentBody` in this folder is a different thing
 * entirely: it renders a typed ARTIFACT from the document model. This one
 * renders bytes fetched from storage.)
 *
 * Two deliberate departures from the Matters reader:
 *
 *   • HTML stays in a SANDBOXED IFRAME (`allow-same-origin`, never
 *     `allow-scripts`). Matters renders it inline behind a tag denylist,
 *     which is a weaker boundary and lets the document's own CSS reach the
 *     app around it.
 *   • Office formats are NOT sent to `view.officeapps.live.com`. That uploads
 *     the file to Microsoft, which is not acceptable for licensed corpus
 *     content or a client's confidential document. We name the format and
 *     offer a download instead of leaking it to a third party.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiDownloadSimpleLight, PiFileLight } from 'react-icons/pi';
import MarkdownStream from '../../Chat/MarkdownStream';
import {
  SOURCE_SANDBOX,
  SourceKind,
  prepareHtmlDocument,
  readableSourceText,
} from '../sourceDocument';

interface Props {
  kind: SourceKind;
  /** The decoded document, for the text-shaped kinds. */
  text?: string;
  /** Object URL for the streamed bytes — what the binary formats render from. */
  url?: string | null;
  title?: string;
  /** Named on the fallback card, so the user knows what they have. */
  fileName?: string;
  contentType?: string;
  frameRef?: React.Ref<HTMLIFrameElement>;
  onFrameLoad?: () => void;
  /**
   * The text/markdown scroller, so the viewer can find and scroll to the
   * cited passage in it — the framed path has `frameRef` for the same job.
   */
  textRef?: React.Ref<HTMLDivElement>;
  /** Marks which locator strategy landed, for the viewer's own state. */
  landed?: string;
}

/** A document we can name but not show — never a blank pane. */
const Unrenderable: React.FC<{
  label: string;
  fileName?: string;
  url?: string | null;
  hint?: string;
}> = ({ label, fileName, url, hint }) => {
  const { t } = useTranslation();
  return (
    <div
      className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center"
      data-testid="source-fallback"
    >
      <PiFileLight className="w-10 h-10 text-fg-muted" aria-hidden />
      <div className="min-w-0">
        <p className="text-sm font-medium text-fg-strong truncate">{fileName || label}</p>
        <p className="mt-0.5 text-xs text-fg-muted">{hint || label}</p>
      </div>
      {url && (
        // The bytes are already in the browser as a blob, so the download
        // costs nothing — and it is the only honest way to open these formats
        // without shipping the file somewhere else.
        <a
          href={url}
          download={fileName || 'document'}
          data-testid="source-download"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs
                     font-medium bg-surface-sunken border border-hairline-soft text-fg-body
                     hover:border-champagne/50 transition-colors duration-200 ease-slide"
        >
          <PiDownloadSimpleLight className="w-3.5 h-3.5" aria-hidden />
          {t('Download')}
        </a>
      )}
    </div>
  );
};

const SourceBody: React.FC<Props> = ({
  kind,
  text = '',
  url,
  title,
  fileName,
  contentType,
  frameRef,
  onFrameLoad,
  textRef,
  landed,
}) => {
  const { t } = useTranslation();

  const srcDoc = useMemo(
    () => (kind === 'html' ? prepareHtmlDocument(text) : ''),
    [kind, text]
  );
  // A JSON-LD blob is lifted to its readable body; everything else passes
  // through untouched. See `readableSourceText`.
  const readable = useMemo(
    () => (kind === 'text' ? readableSourceText(text) : text),
    [kind, text]
  );

  switch (kind) {
    case 'html':
      return (
        <iframe
          title={title || t('Source document')}
          data-testid="source-frame"
          // NEVER `allow-scripts`. `allow-same-origin` alone lets the
          // document's own stylesheet apply while keeping it inert.
          sandbox={SOURCE_SANDBOX}
          referrerPolicy="no-referrer"
          srcDoc={srcDoc}
          ref={frameRef}
          onLoad={onFrameLoad}
          data-landed={landed || ''}
          className="w-full h-full bg-white"
        />
      );

    case 'pdf':
      // The browser's own viewer: multi-page, searchable, printable. Anything
      // we rendered ourselves would be a worse PDF reader.
      return url ? (
        <iframe
          title={title || t('Source document')}
          data-testid="source-pdf"
          src={`${url}#toolbar=1&navpanes=0`}
          className="w-full h-full bg-white"
        />
      ) : (
        <Unrenderable label={t('PDF document')} fileName={fileName} url={url} />
      );

    case 'image':
      return url ? (
        <div className="h-full overflow-auto flex items-center justify-center p-3
                        bg-surface-sunken">
          <img
            src={url}
            alt={title || fileName || t('Source image')}
            data-testid="source-image"
            className="max-w-full h-auto object-contain"
          />
        </div>
      ) : (
        <Unrenderable label={t('Image')} fileName={fileName} url={url} />
      );

    case 'text':
      return (
        <div
          className="h-full overflow-y-auto px-4 py-3"
          data-testid="source-text"
          data-landed={landed || ''}
          ref={textRef}
        >
          <MarkdownStream
            text={readable}
            citations={[]}
            isLive={false}
            onComplete={() => {}}
            onOpenCitation={() => {}}
          />
        </div>
      );

    case 'office':
      return (
        <Unrenderable
          label={t('Office document')}
          fileName={fileName}
          url={url}
          // Said plainly: the alternative would be uploading the file.
          hint={t('This format cannot be previewed here. Download it to read it.')}
        />
      );

    case 'empty':
      return (
        <div className="h-full flex items-center justify-center px-6" data-testid="source-empty">
          <p className="text-xs text-fg-muted">{t('This source has no content to show.')}</p>
        </div>
      );

    default:
      // Binary, or anything we could not classify.
      return (
        <Unrenderable
          label={contentType || t('Unrecognised format')}
          fileName={fileName}
          url={url}
          hint={t('This format cannot be previewed here. Download it to read it.')}
        />
      );
  }
};

export default SourceBody;
